import { useCallback, useMemo, useState } from "react";

function joinUrl(base, path) {
  if (!base) return path;
  const normalizedBase = String(base).replace(/\/+$/, "");
  const normalizedPath = String(path).startsWith(path) ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
}

function getApiBaseUrl() {
  const envBase =
    process.env.REACT_APP_API_BASE ||
    "";

  if (envBase) {
    return envBase.replace(/\/+$/, "");
  }

  if (typeof window !== "undefined") {
    const host = window.location.hostname;

    // local development
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:3001";
    }
  }

  // fallback: same-origin if no explicit backend base URL is configured
  return "";
}

export function useSectionSubmit({ endpoint, method = "POST" }) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const requestUrl = useMemo(() => {
    if (!endpoint) return "";
    if (/^https?:\/\//i.test(endpoint)) return endpoint;
    return joinUrl(apiBaseUrl, endpoint);
  }, [apiBaseUrl, endpoint]);

  const submit = useCallback(
    async (payload) => {
      setStatus("saving");
      setError("");

      try {
        const response = await fetch(requestUrl, {
          method,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload ?? {}),
        });

        const contentType = response.headers.get("content-type") || "";
        const data = contentType.includes("application/json")
          ? await response.json()
          : { ok: response.ok, raw: await response.text() };

        if (!response.ok || data?.ok === false) {
          const message =
            data?.error ||
            data?.message ||
            `Request failed with status ${response.status}`;
          throw new Error(message);
        }

        setStatus("saved");
        return data;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Something went wrong.";
        setError(message);
        setStatus("error");
        return { ok: false, error: message };
      }
    },
    [method, requestUrl]
  );

  return {
    status,
    error,
    submit,
    requestUrl,
  };
}

export default useSectionSubmit;